/**
 * ページアクセス制御の単一定義（SSOT）
 *
 * サイドバーのメニュー表示可否（useNavGroups）と、ルートの直接アクセス可否
 * （ProtectedRoute）の両方がこの定義を参照する。両者を別ロジックで実装すると
 * 「メニューには出ないが URL 直打ちで開けてしまう」という不整合が起きるため、
 * ページごとのアクセス述語をここに一本化する。
 *
 * **画面と API の両方から引くため domain パッケージに置いている。** 画面だけに置くと、
 * 「見られない画面への改善要望が一覧で読めてしまう」ような**サーバ側でも同じ判定が要る場面**で
 * writes が二重実装になる。実際に改善要望の一覧がそれで漏れていた。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business ページ単位のアクセス可否を判定する SSOT。メニュー表示・ルートガード・サーバ側の絞り込みが共通参照する
 * @graph-connects none
 */
import { checkPermission, isInterfaceDesignTeam, isPiDivOrAdmin, } from '@cortex/core-types';
import { isCombatEngineerByTeam, isPdgGroupMember, PMO_TEAM_NAME, } from '@cortex/domain-product-innovation-lab-combat-power-scope';
import { isOpsInternalViewer } from '@cortex/ops-core';
/**
 * IDチーム看板を閲覧できる workspace teamName かを判定する。
 *
 * team コードの値はこの画面が持たない。IDT（designer）は `@cortex/core-types` の
 * `isInterfaceDesignTeam`、PMOチームは combat-power-scope の `PMO_TEAM_NAME` が
 * それぞれ唯一の置き場で、ここは参照するだけ。値を写すとコードが変わったとき
 * 片方だけ直り、許可範囲がサイレントにずれる。
 *
 * IDT 側は `isInterfaceDesignTeam` が前後の空白・大文字を正規化して比べるため、
 * ` pi_idt ` / `PI_IDT` のような未整形の teamName でも所属と判定する。
 * @graph-connects isInterfaceDesignTeam [calls] IDT（designer）所属の判定
 * @graph-connects PMO_TEAM_NAME [references] PMOチームの team コード
 */
function isIdtKpiViewerTeam(teamName) {
    return isInterfaceDesignTeam(teamName) || teamName === PMO_TEAM_NAME;
}
/**
 * 基本設計の画面を試験運用中に見せる人（Firestore users.nickName を小文字で）。
 * 全員に開くときはこの集合ごと消し、`basicDesign` の判定を開放する。
 * @graph-connects none
 */
const BASIC_DESIGN_TRIAL_NICKNAMES = new Set(['lucky']);
/**
 * 指定ページへアクセス可能かを判定する。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business ページキーと実行時コンテキストからアクセス可否を返す。メニュー表示・ルートガード共通の判定関数
 * @graph-connects none
 */
export function canAccessPage(key, ctx) {
    switch (key) {
        // ダッシュボード系メニューの「フォルダ」ノード（KPI / 品質・信頼性をまとめる）。
        // クリックしても遷移せず、いずれかの子ページにアクセスできれば表示する（union）。
        case 'dashboards':
            return canAccessPage('kpi', ctx) || canAccessPage('reliability', ctx);
        // PI Div 横断メニューの「フォルダ」ノード（配下のメンバー・評価系ページをまとめる）。
        // クリックしても遷移せず、いずれかの子ページにアクセスできれば表示する（union。
        // 優先順位ではなく、子の許可範囲の合算がそのまま表示可否になる）。
        // reliability(品質・信頼性)は dashboards フォルダへ移設したため union から除外。
        case 'piDivManagement':
            return (canAccessPage('qa', ctx) ||
                canAccessPage('members', ctx) ||
                canAccessPage('idtKpi', ctx) ||
                canAccessPage('bugEvaluation', ctx) ||
                canAccessPage('qaResponsibility', ctx) ||
                canAccessPage('engineerEvaluation', ctx));
        // PI Div 専用機能。横断 PMO（pi_div 以外 × pmo）はここに入らない（projects と my-page のみ）。
        // reliability(品質・信頼性ページ)は KPI と同じく PI Div/admin 向けのバグ監視ページ。
        // ccCommandMatrix (AI 規定コマンド利用状況) は ccUsage と同じ cc-analyzer 由来の dashboard で、
        // 閲覧範囲も ccUsage と揃える (リーダー / リーダー補佐が数値を見て個別ヒアリングする用途)。
        case 'kpi':
        case 'reliability':
        case 'ccUsage':
        case 'ccCommandMatrix':
        case 'mcpUsage':
            return ctx.isPiDivOrAdmin;
        // コーディング規約 wiki はエンジニア向けの内部ドキュメント。dbDictionary と違い
        // 全社開放はせず、PI Div 所属または admin に閉じる（ビジネスサイドには不要）。
        case 'knowledgeWiki':
            return ctx.isPiDivOrAdmin;
        // 認証済みなら全員（外部社員・横断 PMO 含む）アクセス可能。閲覧は全社員に開放、
        // 編集は API 側の requireDbDictionaryEditor (admin/architect/dle/tech_lead) で個別に縛る。
        case 'dbDictionary':
            return true;
        case 'qa':
            return ctx.isPiDivOrAdmin && ctx.can('VIEW_QA');
        // メンバー管理ページは VIEW_MEMBERS 保持者に加え、TET/ACE のエンジニア全員に開放する。
        // (バックエンド /members は requirePiDivOrAdmin のみで既に許可済み。) IDT designer や
        // PMO は team_name が PDG/ACE 条件外のため含まれない（= IDチーム看板系は対象外）。
        // メンバー管理は piDivManagement フォルダ配下の葉ページ。看板本体は engineerBoard
        // (エンジニア看板) / idtKpi(IDチーム看板) の各葉ページに移設したが、
        // ページ表示可否・member 詳細(/members/$email)のガードは従来の members 判定を維持する。
        // エンジニア看板(engineerBoard)は旧メンバー管理ページ本体の移設先で、アクセス条件は members と同一。
        case 'members':
        case 'engineerBoard':
            return (ctx.isPiDivOrAdmin &&
                (ctx.can('VIEW_MEMBERS') ||
                    isCombatEngineerByTeam({ name: ctx.nickName, team: ctx.teamName })));
        // IDチーム看板: idt(pi_idt) / pmot(pi_pmot) チーム所属者 + 横断 admin。team_name のみで
        // 判定し、nickname allowlist は廃止した（yan を含む全 IDT designer の unified_members
        // team_name が pi_idt に補正済みのため）。members(VIEW_MEMBERS) とは別系統で、member ロールの
        // designer も team 所属だけで閲覧できる。
        case 'idtKpi':
            return ctx.isAdmin || isIdtKpiViewerTeam(ctx.teamName);
        // デザイン成果物検索: ID チーム看板から使う横断検索ツール。IDチーム看板(idtKpi)閲覧者、
        // またはメンバー管理(members)閲覧者に開放する。
        case 'designSearch':
            return canAccessPage('idtKpi', ctx) || canAccessPage('members', ctx);
        // /projects は「フォルダ」ノード（配下 subpage をまとめる親項目）。一覧本体は
        // allPjBoard(全体PJ看板) の葉ページへ移設した。フォルダ表示可否は従来どおり
        // VIEW_PROJECTS で判定する（PMO 全員=横断 PMO 含むに開放）。全体PJ看板(allPjBoard)は
        // 旧 /projects 一覧本体の移設先で、アクセス条件は projects と同一。
        case 'projects':
        case 'allPjBoard':
            return ctx.can('VIEW_PROJECTS');
        // PJ Sandbox（PJ 予定シミュレーション白板）はプロジェクト管理フォルダ配下の葉ページ。
        // VIEW_PROJECTS 保持者（横断 PMO 含む）に加え、PDG/ACE のエンジニア全員に開放する
        // （自分と隣のチームの予定が載った 1 枚の白板を見る用途。githubActivity と同じスコープ）。
        // 開放するのは閲覧だけで、編集権限は付与しない: 保存可否は canEditPjSandbox
        // （admin / pi_div + pmo|team_leader|group_manager）が SSOT で、frontend は
        // GET /pj-sandbox/boards/* が返す canEdit で編集導線を出し分け、backend は PUT を 403 で弾く。
        case 'pjSandbox':
            return (ctx.can('VIEW_PROJECTS') ||
                isCombatEngineerByTeam({ name: ctx.nickName, team: ctx.teamName }));
        // 要件定義の成果物は、案件に入る全員（エンジニア・PL・デザイナー・CS）が読む前提の
        // 入口なので、認証済みなら誰でも閲覧できる。書き込みは MCP 側で親・子チケットの担当者に絞る。
        case 'specRequirements':
            return true;
        // 改善要望は PI Lab を使う全員が出す前提の入口なので、認証済みなら誰でも見られる。
        // 出すのも誰でもできる（判定は API 側）。
        case 'improvements':
            return true;
        // 組織課題（看板）は特定の組織に閉じない汎用の仕組みなので、画面は全員に開く。
        // どのボード・課題が見られるかは、ボードの公開範囲で API（domain-org-issues）が判定する。
        case 'orgIssues':
            return true;
        // 週次MTG も組織課題と同じく汎用の仕組みなので、画面は全員に開く。
        // どのシリーズ・開催回が見られるかは、シリーズの公開範囲で API（domain-team-meeting）が判定する。
        case 'teamMeetings':
            return true;
        // CuttySark 賞は PDG（プロダクトグループ）の予選・本戦の進行に使うので、PDG グループ全員
        // （チームは問わない）と admin に絞る。API の /cuttysark/* も同じ判定で 403 にする。
        // 運営操作・投票の可否はさらにセッションごとに API 側（cuttysark-rules）で判定する。
        case 'cuttysark':
            return ctx.isAdmin || isPdgGroupMember(ctx.groupId) || isPdgGroupMember(ctx.groupName);
        // 基本設計は本番データで試験運用中のため、画面は試験する人だけに出す（admin にも出さない）。
        // API 側はこの判定を通さないので、ここで閉じても MCP は動く。要件定義と case を分けるのは、
        // 片方の閲覧範囲を変えたときにもう片方が一緒に変わらないようにするため。
        case 'basicDesign':
            return BASIC_DESIGN_TRIAL_NICKNAMES.has((ctx.nickName ?? '').toLowerCase());
        // PMO看板は pi_pmot チーム or admin/group_manager のみ。横断 PMO は team!=pi_pmot なので不可。
        // ただし「実績集計」タブだけは PDG 全チームの team_leader にも開いており
        // （VIEW_PMO_PERFORMANCE_AGG）、その人たちもページ自体には入れる必要がある。
        // **ページに入れる = 全タブが見える、ではない。** 実績集計しか権限が無い人には
        // 画面側（projects.pmo-board.tsx）が実績集計タブだけを出し、他タブの API も
        // assertPmoBoardAccess で 403 になる。
        case 'pmoBoard':
            return ctx.can('VIEW_PMO_BOARD') || ctx.can('VIEW_PMO_PERFORMANCE_AGG');
        // アサイン履歴はリーダー以上と PMO（pmo ロール + PMOT 所属者）。API の
        // /projects/assignment-history も同じ VIEW_ASSIGNMENT_HISTORY で 403 にする。
        case 'assignmentHistory':
            return ctx.can('VIEW_ASSIGNMENT_HISTORY');
        // 軽タスクリストは全社共有の待機列。認証済みなら全員（外部社員・横断 PMO 含む）が閲覧可能。
        // セルフ・アサインの「軽タスクをリクエストする」ボタンはページ内でエンジニア/PMO のみに絞る。
        case 'lightTasks':
            return true;
        // aC/Mall/Logi Div看板は事業部向け案件を横断 PMO ビューでスコープ表示する。PMO看板と同じく
        // PI Div の PMOチーム（pi_pmot）or admin/group_manager のみ閲覧可能（VIEW_PMO_BOARD）。
        // 横断 PMO（ac_div/mall_div/log_div × pmo）は team!=pi_pmot なので自分以外の Div も含めて不可。
        case 'acDivBoard':
        case 'mallDivBoard':
        case 'logiDivBoard':
            return ctx.can('VIEW_PMO_BOARD');
        case 'bugEvaluation':
            return ctx.isPiDivOrAdmin && ctx.can('VIEW_BUG_EVALUATION');
        // QA責任評価は i-point とは別画面。参照は PI Div のエンジニア組織に閉じる。
        // VIEW_QA_RESPONSIBILITY は全 team_leader を含む（QAチーム限定を撤廃）ため、
        // 「PI Div 所属の team_leader に限定」は bugEvaluation と同じく isPiDivOrAdmin で enforce する。
        case 'qaResponsibility':
            return ctx.isPiDivOrAdmin && ctx.can('VIEW_QA_RESPONSIBILITY');
        case 'engineerEvaluation':
            return ctx.isPiDivOrAdmin && ctx.can('VIEW_ENGINEER_EVALUATION');
        case 'accounts':
            return ctx.isPiDivOrAdmin && ctx.can('MANAGE_ACCOUNTS');
        // チームの担当（DLE 等）。**参照は内部メンバー全員に開く。** 誰が要件定義レビューの
        // 担当かは案件を進める全員が知りたい情報で、隠すと毎回人に聞くことになる。
        // 書き込みだけを MANAGE_TEAM_ASSIGNMENTS（CTO / PMO）に絞るのは画面と API の側。
        case 'teamAssignments':
            return ctx.isInternalMember;
        // GitHub Activity は PI Div 所属者・admin に加え、PDG/ACE のエンジニア全員に開放する。
        // VIEW_MEMBERS 権限や daily-report target 登録の有無は問わない。
        case 'githubActivity':
            return (ctx.isPiDivOrAdmin || isCombatEngineerByTeam({ name: ctx.nickName, team: ctx.teamName }));
        // SRE/インフラ系メニューの「フォルダ」ノード（CC Usage / MCP Usage をまとめる）。
        case 'sreManagement':
            return canAccessPage('ccUsage', ctx);
        // 認証済みなら全員（外部社員・横断 PMO 含む）アクセス可能な入口ページ
        case 'myPage':
            return true;
        // オペレーション（opetan）: 社内従業員のみ read 開放。API の requireOpsViewer と同一述語を使い、
        // サイドバー表示と /ops/* の許可範囲を一致させる（除外理由は isOpsInternalViewer 側に記載）。
        case 'operations':
            return isOpsInternalViewer(ctx.division, { isAdmin: ctx.isAdmin });
    }
}
/**
 * 改善要望の対象にできる画面（＝その人が見られる画面）を返す。
 *
 * **画面と API の両方がこれを引く。** 選択肢を画面側だけで絞っても、API を直接叩けば
 * 見られない画面への要望を出せるし、一覧も読めてしまう。要望の本文は人が自由に書くので、
 * 見られない画面（アカウント管理・評価・バグ評価など）の中身がそこに書かれうる。
 *
 * `whole`（どの画面でもない話）は誰でも選べる——横断の使い勝手の話に権限は要らない。
 * @graph-connects none
 */
export function visiblePilabAreas(areas, ctx) {
    return areas.filter((area) => area === 'whole' || canAccessPage(area, ctx));
}
/**
 * その人が改善要望の対象にできる画面か。
 * @graph-connects none
 */
export function canTargetPilabArea(area, ctx) {
    return area === 'whole' || canAccessPage(area, ctx);
}
/**
 * ユーザー情報から `PageAccessContext` を組み立てる。
 *
 * **画面と API が同じ材料から同じ文脈を作るための 1 箇所。** `isInternalMember` や
 * `isAdmin` の導出をそれぞれの入口で書くと、片方だけ条件が変わったときに
 * 「画面では見えないが API からは読める」がサイレントに生まれる。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business ユーザー情報からページアクセス判定の文脈を組み立てる。画面とAPIが同じ判定を引くための共通入口
 * @graph-connects checkPermission [calls] ポータル権限の判定
 * @graph-connects isPiDivOrAdmin [calls] PI Div 所属・admin の判定
 */
export function buildPageAccessContext(user) {
    const roles = user?.portalRoles ?? [];
    const teamName = user?.teamName ?? undefined;
    return {
        can: (permission) => checkPermission(roles, permission, teamName),
        isInternalMember: roles.some((role) => role !== 'external'),
        isPiDivOrAdmin: isPiDivOrAdmin(user?.division, roles),
        isAdmin: roles.includes('admin'),
        teamName: user?.teamName,
        nickName: user?.nickName,
        division: user?.division,
        groupId: user?.groupId,
        groupName: user?.groupName,
    };
}
//# sourceMappingURL=page-access.js.map